// Reform API client.
//
// Two flavors of helpers because Next.js App Router has two execution
// environments and they need different fetch shapes:
//
//   - Server-side helpers (`fetchProjectWithScreens`, `fetchMe`, ...) run
//     inside a Next.js server component or route handler. They reach the
//     backend over the docker-internal hostname (API_BASE_URL) and forward
//     the session cookie they read from next/headers.
//
//   - Browser-side helpers (`postLogin`, `postSignup`, `postGenerateEdit`)
//     run in the user's browser. They use the host-port-mapped URL and
//     credentials: "include" so cookies set by the backend get attached.
//
// The split avoids two real bugs we've already hit:
//   1) localhost from inside a docker container doesn't reach the backend
//      container (so server-side fetches need API_BASE_URL=http://backend:4000)
//   2) An HttpOnly session cookie set by the backend can't be read in JS --
//      the browser attaches it automatically when credentials: "include" is set.

import type {
  ApiResponse,
  ProjectWithScreens,
  ProjectListResponse,
  ProjectRecord,
  ScreenRecord,
  GenerateEditRequest,
  GenerateEditResponse,
  IngestBrandRequest,
  GenerateLayoutRequest,
  RegenerateScreenRequest,
  RegenerateProjectRequest,
  RegenerateProjectResult,
  SignupRequest,
  LoginRequest,
  UserRecord,
  CreateTenantRequest,
  SwitchTenantRequest,
  TenantMembership,
  DeployProjectRequest,
  DeploymentRecord,
  UpdateProjectRequest,
  LayoutSchema,
  SeoSettings,
  DesignTokens,
  CollectionRecord,
  CollectionItemRecord,
  CreateCollectionRequest,
  UpdateCollectionRequest,
  CreateCollectionItemRequest,
  UpdateCollectionItemRequest,
  ImportCollectionsRequest,
  ImportCollectionsResponse,
  ExportCollectionRequest,
  ExportCollectionResponse,
  MembersListResponse,
  InvitationRecord,
  InvitationPreview,
  InviteMemberRequest,
  TenantRole,
  DeploymentListResponse,
  UpdateScreenRequest,
} from "@reform/shared";

// Docker-internal URL for server components. Defaults to the docker service
// name; can be overridden for local-shell runs that target localhost:4000.
const API_BASE_URL = process.env.API_BASE_URL ?? "http://backend:4000";

// Host-port URL for browser-initiated requests. Falls back to localhost
// which is right for both the dev harness and the dockerized frontend
// (browser code runs in the user's browser, not the container).
const BROWSER_API_BASE_URL =
  process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:4000";

export class ApiClientError extends Error {
  constructor(
    message: string,
    public readonly status: number,
  ) {
    super(message);
    this.name = "ApiClientError";
  }
}

// ---------------------------------------------------------------------------
// Server-side
// ---------------------------------------------------------------------------

interface ServerFetchOpts {
  /** Forwarded `Cookie:` header value, read by the caller from next/headers. */
  cookieHeader?: string;
  /** Skip auth -- used by `fetchMe` when probing whether the user is signed in. */
  okStatuses?: number[];
}

async function serverGet<T>(
  path: string,
  opts: ServerFetchOpts = {},
): Promise<T> {
  const res = await fetch(`${API_BASE_URL}${path}`, {
    method: "GET",
    headers: {
      Accept: "application/json",
      ...(opts.cookieHeader ? { Cookie: opts.cookieHeader } : {}),
    },
    cache: "no-store",
  });

  const body = (await res.json()) as ApiResponse<T>;
  if (!res.ok || !body.success) {
    const errMsg = body.success ? `HTTP ${res.status}` : body.error;
    throw new ApiClientError(errMsg, res.status);
  }
  return body.data;
}

/**
 * GET /api/projects/:id -- canvas hydration payload. Server-side only.
 */
export function fetchProjectWithScreens(
  projectId: string,
  cookieHeader: string | undefined,
): Promise<ProjectWithScreens> {
  return serverGet<ProjectWithScreens>(`/api/projects/${projectId}`, {
    cookieHeader,
  });
}

// Lightweight poll target for background brand ingest (browser-side).
export interface BrandStatusInfo {
  brand_status: "pending" | "ready" | "failed";
  design_tokens: import("@reform/shared").DesignTokens;
  warning: string | null;
  /** Art direction chosen with the brand (Studio Engine). */
  art_direction?: import("@reform/shared").ArtDirection | null;
}

/**
 * PATCH /api/projects/:id/art-direction -- switch design language or tune the
 * experience layer. Instant re-skin, no regeneration.
 */
export function patchArtDirection(
  projectId: string,
  body: import("@reform/shared").UpdateArtDirectionRequest,
): Promise<import("@reform/shared").ArtDirection> {
  return browserRequest("PATCH", `/api/projects/${projectId}/art-direction`, body);
}
export function fetchBrandStatus(projectId: string): Promise<BrandStatusInfo> {
  return browserRequest<BrandStatusInfo>("GET", `/api/projects/${projectId}/brand`);
}

/**
 * GET /api/projects -- list every project visible to the caller's tenant.
 * Server-side only.
 */
export function fetchMyProjects(
  cookieHeader: string | undefined,
): Promise<ProjectListResponse> {
  return serverGet<ProjectListResponse>(`/api/projects`, { cookieHeader });
}

/**
 * GET /api/auth/me -- returns null on 401 instead of throwing, so callers
 * (root layout, middleware) can branch on "signed in or not" cleanly.
 */
export async function fetchMe(
  cookieHeader: string | undefined,
): Promise<UserRecord | null> {
  try {
    return await serverGet<UserRecord>(`/api/auth/me`, { cookieHeader });
  } catch (err) {
    if (err instanceof ApiClientError && err.status === 401) return null;
    throw err;
  }
}

// ---------------------------------------------------------------------------
// Browser-side
// ---------------------------------------------------------------------------

async function browserRequest<T>(
  method: "POST" | "GET" | "PATCH" | "PUT" | "DELETE",
  path: string,
  body?: unknown,
): Promise<T> {
  const res = await fetch(`${BROWSER_API_BASE_URL}${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    // Browser attaches the HttpOnly session cookie iff credentials: "include".
    credentials: "include",
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  let parsed: ApiResponse<T>;
  try {
    parsed = (await res.json()) as ApiResponse<T>;
  } catch {
    throw new ApiClientError(`Non-JSON response (HTTP ${res.status})`, res.status);
  }
  if (!res.ok || !parsed.success) {
    const errMsg = parsed.success ? `HTTP ${res.status}` : parsed.error;
    throw new ApiClientError(errMsg, res.status);
  }
  return parsed.data;
}

export function postSignup(body: SignupRequest): Promise<UserRecord> {
  return browserRequest<UserRecord>("POST", "/api/auth/signup", body);
}

export function postLogin(body: LoginRequest): Promise<UserRecord> {
  return browserRequest<UserRecord>("POST", "/api/auth/login", body);
}

export function postLogout(): Promise<{ ok: boolean }> {
  return browserRequest<{ ok: boolean }>("POST", "/api/auth/logout");
}

/**
 * Ask the backend to re-send the email-verification link. Always resolves
 * (the endpoint returns 200 regardless of whether the account exists/needs it),
 * so callers can show a single neutral "check your inbox" message.
 */
export function resendVerification(
  email: string,
): Promise<{ ok: boolean; message: string }> {
  return browserRequest<{ ok: boolean; message: string }>(
    "POST",
    "/api/auth/resend-verification",
    { email },
  );
}

export function postGenerateEdit(
  body: GenerateEditRequest,
): Promise<GenerateEditResponse> {
  return browserRequest<GenerateEditResponse>(
    "POST",
    "/api/generate/edit",
    body,
  );
}

/**
 * POST /api/ingest/brand -- scrape a URL, derive design_tokens, create
 * (or update) a Project. Used by the in-product project-creation flow.
 */
export function postIngestBrand(
  body: IngestBrandRequest,
): Promise<ProjectRecord> {
  return browserRequest<ProjectRecord>("POST", "/api/ingest/brand", body);
}

/**
 * POST /api/generate/layout -- generate a Screen for an existing Project.
 * Used after ingest to mint the first homepage.
 */
export function postGenerateLayout(
  body: GenerateLayoutRequest,
): Promise<ScreenRecord> {
  return browserRequest<ScreenRecord>("POST", "/api/generate/layout", body);
}

/**
 * POST /api/generate/regenerate-screen -- re-run generation for an existing
 * screen from its originally-recorded prompt, replacing its layout_schema in
 * place. Destructive: overwrites any manual canvas edits. Caller confirms first.
 */
export function postRegenerateScreen(
  body: RegenerateScreenRequest,
): Promise<ScreenRecord> {
  return browserRequest<ScreenRecord>(
    "POST",
    "/api/generate/regenerate-screen",
    body,
  );
}

/**
 * POST /api/generate/regenerate-project -- regenerate every screen in the
 * project that has a recorded prompt. Can take a while for large projects
 * (sequential LLM calls, one per screen).
 */
export function postRegenerateProject(
  body: RegenerateProjectRequest,
): Promise<RegenerateProjectResult> {
  return browserRequest<RegenerateProjectResult>(
    "POST",
    "/api/generate/regenerate-project",
    body,
  );
}

/**
 * POST /api/tenants -- create a new workspace; the caller becomes OWNER.
 * Returns the new membership (is_current is always false; switch afterward
 * if you want to drop the user into it).
 */
export function postCreateTenant(
  body: CreateTenantRequest,
): Promise<TenantMembership> {
  return browserRequest<TenantMembership>("POST", "/api/tenants", body);
}

/**
 * POST /api/tenants/switch -- flip the active tenant on this session.
 */
export function postSwitchTenant(
  body: SwitchTenantRequest,
): Promise<TenantMembership> {
  return browserRequest<TenantMembership>(
    "POST",
    "/api/tenants/switch",
    body,
  );
}

/**
 * POST /api/deploy/project -- kick off an async deploy. Returns the
 * Deployment row in PENDING state; the runner moves it through
 * RUNNING -> SUCCEEDED / FAILED. Use `fetchDeployment` to poll for status.
 */
export function postDeployProject(
  body: DeployProjectRequest,
): Promise<DeploymentRecord> {
  return browserRequest<DeploymentRecord>("POST", "/api/deploy/project", body);
}

// --- Project & screen management -------------------------------------------

/** PATCH /api/projects/:id -- rename a project. */
export function patchProject(
  id: string,
  body: UpdateProjectRequest,
): Promise<ProjectRecord> {
  return browserRequest<ProjectRecord>("PATCH", `/api/projects/${id}`, body);
}

/** DELETE /api/projects/:id -- delete a project (cascades screens/assets). */
export function deleteProject(
  id: string,
): Promise<{ deleted: boolean; project_id: string }> {
  return browserRequest("DELETE", `/api/projects/${id}`);
}

/** POST /api/projects/:id/duplicate -- clone a project as a new DRAFT. */
export function duplicateProject(id: string): Promise<ProjectRecord> {
  return browserRequest<ProjectRecord>(
    "POST",
    `/api/projects/${id}/duplicate`,
  );
}

/** DELETE /api/projects/:id/screens/:screenId -- remove one screen. */
export function deleteScreen(
  projectId: string,
  screenId: string,
): Promise<{ deleted: boolean; screen_id: string }> {
  return browserRequest(
    "DELETE",
    `/api/projects/${projectId}/screens/${screenId}`,
  );
}

/** PATCH /api/projects/:id/seo -- save the project's SEO/AEO settings. */
export function saveSeoSettings(
  projectId: string,
  settings: SeoSettings,
): Promise<SeoSettings> {
  return browserRequest("PATCH", `/api/projects/${projectId}/seo`, settings);
}

/** PATCH /api/projects/:id/tokens -- save the project's brand colors/fonts. */
export function saveDesignTokens(
  projectId: string,
  tokens: DesignTokens,
): Promise<DesignTokens> {
  return browserRequest("PATCH", `/api/projects/${projectId}/tokens`, tokens);
}

/** PUT .../screens/:screenId/layout -- persist a full layoutSchema (auto-save). */
export function saveScreenLayout(
  projectId: string,
  screenId: string,
  layoutSchema: LayoutSchema,
): Promise<{ saved: boolean; screen_id: string }> {
  return browserRequest(
    "PUT",
    `/api/projects/${projectId}/screens/${screenId}/layout`,
    { layout_schema: layoutSchema },
  );
}

/**
 * GET /api/deployments/:id -- poll the status of an in-flight (or finished)
 * deploy. Returns the same DeploymentRecord shape.
 */
export function fetchDeployment(id: string): Promise<DeploymentRecord> {
  return browserRequest<DeploymentRecord>("GET", `/api/deployments/${id}`);
}

// ---- Collections (CMS) -----------------------------------------------------

export function listCollections(
  projectId: string,
): Promise<{ collections: CollectionRecord[] }> {
  return browserRequest("GET", `/api/collections?project_id=${projectId}`);
}

export function createCollection(
  body: CreateCollectionRequest,
): Promise<CollectionRecord> {
  return browserRequest<CollectionRecord>("POST", "/api/collections", body);
}

export function updateCollection(
  id: string,
  body: UpdateCollectionRequest,
): Promise<CollectionRecord> {
  return browserRequest<CollectionRecord>("PATCH", `/api/collections/${id}`, body);
}

export function deleteCollection(id: string): Promise<unknown> {
  return browserRequest("DELETE", `/api/collections/${id}`);
}

export function listCollectionItems(
  collectionId: string,
): Promise<{ items: CollectionItemRecord[] }> {
  return browserRequest("GET", `/api/collections/${collectionId}/items`);
}

export function createCollectionItem(
  collectionId: string,
  body: CreateCollectionItemRequest,
): Promise<CollectionItemRecord> {
  return browserRequest<CollectionItemRecord>(
    "POST",
    `/api/collections/${collectionId}/items`,
    body,
  );
}

export function updateCollectionItem(
  collectionId: string,
  itemId: string,
  body: UpdateCollectionItemRequest,
): Promise<CollectionItemRecord> {
  return browserRequest<CollectionItemRecord>(
    "PATCH",
    `/api/collections/${collectionId}/items/${itemId}`,
    body,
  );
}

export function deleteCollectionItem(
  collectionId: string,
  itemId: string,
): Promise<unknown> {
  return browserRequest("DELETE", `/api/collections/${collectionId}/items/${itemId}`);
}

export function importCollections(
  body: ImportCollectionsRequest,
): Promise<ImportCollectionsResponse> {
  return browserRequest<ImportCollectionsResponse>(
    "POST",
    "/api/collections/import",
    body,
  );
}

export function exportCollection(
  collectionId: string,
  body: ExportCollectionRequest,
): Promise<ExportCollectionResponse> {
  return browserRequest<ExportCollectionResponse>(
    "POST",
    `/api/collections/${collectionId}/export`,
    body,
  );
}

// ---------------------------------------------------------------------------
// Custom domains (Phase 9)
// ---------------------------------------------------------------------------

/**
 * Mirrors backend/src/services/vercelDeployer.ts CustomDomainStatus. Lives
 * here (not @reform/shared) because it's a passthrough of Vercel's shape,
 * owned by the deployer service, not an engine schema.
 */
export interface DomainVerificationRecord {
  type: string;
  domain: string;
  value: string;
  reason?: string;
}

export interface CustomDomainStatus {
  domain: string;
  verified: boolean;
  misconfigured: boolean;
  verification: DomainVerificationRecord[];
}

/** GET /api/projects/:id/domains -- all domains on the deployed project. */
export function fetchProjectDomains(
  projectId: string,
): Promise<{ domains: CustomDomainStatus[] }> {
  return browserRequest<{ domains: CustomDomainStatus[] }>(
    "GET",
    `/api/projects/${projectId}/domains`,
  );
}

/** POST /api/projects/:id/domains -- attach a domain (returns DNS records). */
export function addProjectDomain(
  projectId: string,
  domain: string,
): Promise<CustomDomainStatus> {
  return browserRequest<CustomDomainStatus>(
    "POST",
    `/api/projects/${projectId}/domains`,
    { domain },
  );
}

/** POST .../verify -- ask Vercel to re-check DNS now. */
export function verifyProjectDomain(
  projectId: string,
  domain: string,
): Promise<CustomDomainStatus> {
  return browserRequest<CustomDomainStatus>(
    "POST",
    `/api/projects/${projectId}/domains/${encodeURIComponent(domain)}/verify`,
  );
}

/** DELETE -- detach a domain from the project. */
export function removeProjectDomain(
  projectId: string,
  domain: string,
): Promise<{ domain: string; removed: boolean }> {
  return browserRequest<{ domain: string; removed: boolean }>(
    "DELETE",
    `/api/projects/${projectId}/domains/${encodeURIComponent(domain)}`,
  );
}

/** POST /api/assets/upload -- store a user image (data URI), get its URL. */
export function uploadAsset(
  dataUri: string,
  filename?: string,
): Promise<{ url: string; content_type: string; bytes: number }> {
  return browserRequest<{ url: string; content_type: string; bytes: number }>(
    "POST",
    "/api/assets/upload",
    { data_uri: dataUri, filename },
  );
}

// ---------------------------------------------------------------------------
// Members + invitations
// ---------------------------------------------------------------------------

export function fetchMembers(): Promise<MembersListResponse> {
  return browserRequest<MembersListResponse>("GET", "/api/members");
}

export function inviteMember(body: InviteMemberRequest): Promise<InvitationRecord> {
  return browserRequest<InvitationRecord>("POST", "/api/members/invite", body);
}

export function revokeInvitation(id: string): Promise<{ revoked: boolean }> {
  return browserRequest<{ revoked: boolean }>("DELETE", `/api/members/invitations/${id}`);
}

export function updateMemberRole(userId: string, role: TenantRole): Promise<{ user_id: string; role: TenantRole }> {
  return browserRequest<{ user_id: string; role: TenantRole }>("PATCH", `/api/members/${userId}`, { role });
}

export function removeMember(userId: string): Promise<{ removed: boolean }> {
  return browserRequest<{ removed: boolean }>("DELETE", `/api/members/${userId}`);
}

/** Public preview of an invitation (no session required). */
export function fetchInvitationPreview(token: string): Promise<InvitationPreview> {
  return browserRequest<InvitationPreview>("GET", `/api/auth/invitation/${encodeURIComponent(token)}`);
}

export function acceptInvite(token: string): Promise<TenantMembership> {
  return browserRequest<TenantMembership>("POST", "/api/tenants/accept-invite", { token });
}

// ---------------------------------------------------------------------------
// Deploy history + screen management
// ---------------------------------------------------------------------------

export function fetchDeploymentHistory(projectId: string): Promise<DeploymentListResponse> {
  return browserRequest<DeploymentListResponse>("GET", `/api/deployments/project/${projectId}`);
}

export function updateScreen(
  projectId: string,
  screenId: string,
  body: UpdateScreenRequest,
): Promise<{ screen_id: string; title: string; route_path: string }> {
  return browserRequest<{ screen_id: string; title: string; route_path: string }>(
    "PATCH",
    `/api/projects/${projectId}/screens/${screenId}`,
    body,
  );
}

export function reorderScreens(projectId: string, screenIds: string[]): Promise<{ reordered: boolean }> {
  return browserRequest<{ reordered: boolean }>("POST", `/api/projects/${projectId}/screens/reorder`, {
    screen_ids: screenIds,
  });
}

// ---------------------------------------------------------------------------
// Preview sharing + project move
// ---------------------------------------------------------------------------

export function createPreviewToken(
  projectId: string,
  screenId: string,
): Promise<{ preview_path: string }> {
  return browserRequest<{ preview_path: string }>(
    "POST",
    `/api/projects/${projectId}/screens/${screenId}/preview-token`,
  );
}

export function revokePreviewToken(
  projectId: string,
  screenId: string,
): Promise<{ revoked: boolean }> {
  return browserRequest<{ revoked: boolean }>(
    "DELETE",
    `/api/projects/${projectId}/screens/${screenId}/preview-token`,
  );
}

export function moveProject(
  projectId: string,
  targetTenantId: string,
): Promise<ProjectRecord> {
  return browserRequest<ProjectRecord>("POST", `/api/projects/${projectId}/move`, {
    target_tenant_id: targetTenantId,
  });
}

/** GET /api/tenants -- the caller's workspace memberships. */
export function fetchTenants(): Promise<{ memberships: TenantMembership[] }> {
  return browserRequest<{ memberships: TenantMembership[] }>("GET", "/api/tenants");
}

// ---------------------------------------------------------------------------
// Billing + support + admin
// ---------------------------------------------------------------------------

export interface BillingInfo {
  plan: string;
  plan_name: string;
  plan_override: boolean;
  subscription_status: string | null;
  renews_at: string | null;
  usage: { projects: number; members: number };
  limits: { projects: number; screensPerProject: number; members: number; customDomains: boolean };
  checkout_configured: boolean;
}

export function fetchBilling(): Promise<BillingInfo> {
  return browserRequest<BillingInfo>("GET", "/api/billing");
}

export function createCheckout(tier: "PRO" | "STUDIO"): Promise<{ checkout_url: string }> {
  return browserRequest<{ checkout_url: string }>("POST", "/api/billing/checkout", { tier });
}

// ---- Credits + model selection -------------------------------------------

export interface CreditLedgerEntry {
  id: string;
  delta: number;
  balance_after: number;
  kind: string;
  model_id: string | null;
  note: string | null;
  created_at: string;
}
export interface CreditsInfo {
  balance: number;
  selected_model: string;
  models: import("@reform/shared").ModelDef[];
  packs: import("@reform/shared").CreditPack[];
  ledger: CreditLedgerEntry[];
  checkout_configured: boolean;
}

export function fetchCredits(): Promise<CreditsInfo> {
  return browserRequest<CreditsInfo>("GET", "/api/credits");
}
export function selectModel(modelId: string): Promise<{ selected_model: string }> {
  return browserRequest<{ selected_model: string }>("POST", "/api/credits/model", { model_id: modelId });
}
export function createCreditCheckout(packId: string): Promise<{ checkout_url: string; pack: string; credits: number }> {
  return browserRequest<{ checkout_url: string; pack: string; credits: number }>(
    "POST",
    "/api/credits/checkout",
    { pack_id: packId },
  );
}

export interface TicketMessage { id: string; from_admin: boolean; body: string; created_at: string }
export interface Ticket {
  id: string; subject: string; status: "OPEN" | "CLOSED";
  created_at: string; updated_at: string; messages: TicketMessage[];
  tenant_name?: string; tenant_plan?: string; tenant_id?: string;
}

export function fetchMyTickets(): Promise<{ tickets: Ticket[] }> {
  return browserRequest<{ tickets: Ticket[] }>("GET", "/api/support");
}
export function createTicket(subject: string, body: string): Promise<{ id: string }> {
  return browserRequest<{ id: string }>("POST", "/api/support", { subject, body });
}
export function replyTicket(id: string, body: string): Promise<{ replied: boolean }> {
  return browserRequest<{ replied: boolean }>("POST", `/api/support/${id}/reply`, { body });
}

export interface AdminTenant {
  tenant_id: string; name: string; plan: string; plan_override: boolean;
  subscription_status: string | null; renews_at: string | null;
  projects: number; members: number; owner_email: string | null; created_at: string;
}
export interface AdminBillingEvent {
  id: string; tenant_id: string | null; type: string; created_at: string;
  total_formatted: string | null; status: string | null; receipt_url: string | null;
  customer_email: string | null;
}
export function adminFetchTenants(): Promise<{ tenants: AdminTenant[] }> {
  return browserRequest<{ tenants: AdminTenant[] }>("GET", "/api/admin/tenants");
}
export function adminSetPlan(tenantId: string, plan: string, override: boolean): Promise<unknown> {
  return browserRequest("PATCH", `/api/admin/tenants/${tenantId}/plan`, { plan, override });
}
export function adminFetchBillingEvents(): Promise<{ events: AdminBillingEvent[] }> {
  return browserRequest<{ events: AdminBillingEvent[] }>("GET", "/api/admin/billing-events");
}
export function adminFetchTickets(): Promise<{ tickets: Ticket[] }> {
  return browserRequest<{ tickets: Ticket[] }>("GET", "/api/admin/tickets");
}
export function adminReplyTicket(id: string, body: string): Promise<unknown> {
  return browserRequest("POST", `/api/admin/tickets/${id}/reply`, { body });
}
export function adminSetTicketStatus(id: string, status: "OPEN" | "CLOSED"): Promise<unknown> {
  return browserRequest("PATCH", `/api/admin/tickets/${id}`, { status });
}

export interface AdminAuditEvent {
  id: string; action: string; tenant_id: string | null; user_id: string | null;
  actor_email: string | null; ip: string | null;
  metadata: unknown; created_at: string;
}
export function adminFetchAudit(action?: string): Promise<{ events: AdminAuditEvent[] }> {
  const q = action ? `?action=${encodeURIComponent(action)}` : "";
  return browserRequest<{ events: AdminAuditEvent[] }>("GET", `/api/admin/audit${q}`);
}

export interface AdminVisitor {
  id: string; project_id: string; project_name: string; tenant_id: string;
  email: string; display_name: string | null; active_sessions: number; created_at: string;
}
export function adminFetchVisitors(): Promise<{ visitors: AdminVisitor[] }> {
  return browserRequest<{ visitors: AdminVisitor[] }>("GET", "/api/admin/visitors");
}

// Lead intake (POST /api/v1/intake/pages) -- keys and inbound leads.
export interface AdminIntakeKey {
  id: string; name: string; prefix: string; tenant_id: string;
  rate_limit_per_min: number; daily_cap: number; require_signature: boolean;
  jobs: number; last_used_at: string | null; revoked_at: string | null; created_at: string;
}
export interface AdminIntakeLead {
  id: string; status: "QUEUED" | "RUNNING" | "READY" | "FAILED" | "DEAD";
  failure_reason: string | null; attempts: number;
  contact_name: string; contact_email: string; company: string | null; project_idea: string;
  reference_url: string | null; relationship: string; language: string | null;
  marketing_consent: boolean; source_site: string; source_reference: string; key_name: string;
  project_id: string | null; preview_url: string | null; preview_expires_at: string | null;
  cost: { scrapes: number; llm_calls: number; images: number };
  created_at: string; finished_at: string | null; delete_after: string;
}
export function adminFetchIntakeKeys(): Promise<{ keys: AdminIntakeKey[] }> {
  return browserRequest<{ keys: AdminIntakeKey[] }>("GET", "/api/admin/intake/keys");
}
export function adminCreateIntakeKey(body: {
  name: string; rate_limit_per_min?: number; daily_cap?: number; require_signature?: boolean;
}): Promise<{ id: string; name: string; key: string; signing_secret: string | null; prefix: string }> {
  return browserRequest("POST", "/api/admin/intake/keys", body);
}
export function adminRevokeIntakeKey(id: string): Promise<unknown> {
  return browserRequest("DELETE", `/api/admin/intake/keys/${id}`);
}
export function adminFetchIntakeLeads(): Promise<{ leads: AdminIntakeLead[] }> {
  return browserRequest<{ leads: AdminIntakeLead[] }>("GET", "/api/admin/intake/leads");
}
export function adminDeleteIntakeLead(id: string): Promise<unknown> {
  return browserRequest("DELETE", `/api/admin/intake/leads/${id}`);
}
export function adminIntakePreview(
  id: string,
  action: "extend" | "rotate" | "revoke",
  days?: number,
): Promise<{ preview_url: string | null; preview_expires_at: string | null }> {
  return browserRequest("POST", `/api/admin/intake/leads/${id}/preview`, { action, ...(days ? { days } : {}) });
}
export function adminRetryIntakeLead(id: string): Promise<unknown> {
  return browserRequest("POST", `/api/admin/intake/leads/${id}/retry`);
}

// Owner-facing member-area visitors for a single project.
export interface SiteVisitorRow {
  id: string;
  email: string;
  display_name: string | null;
  active_sessions: number;
  created_at: string;
}
export function listProjectVisitors(
  projectId: string,
): Promise<{ visitors: SiteVisitorRow[] }> {
  return browserRequest<{ visitors: SiteVisitorRow[] }>(
    "GET",
    `/api/projects/${projectId}/visitors`,
  );
}
export function deleteProjectVisitor(
  projectId: string,
  visitorId: string,
): Promise<unknown> {
  return browserRequest(
    "DELETE",
    `/api/projects/${projectId}/visitors/${visitorId}`,
  );
}

// Downloadable project files (optionally members-only). `data` is a base64 data
// URI. The returned `url` is the gated serve URL to use as a download href.
export interface ProjectFile {
  id: string;
  file_name: string;
  mime_type: string;
  size_bytes: number;
  members_only: boolean;
  url: string;
  created_at: string;
}
export function uploadProjectFile(
  projectId: string,
  data: string,
  fileName: string,
  membersOnly: boolean,
): Promise<ProjectFile> {
  return browserRequest<ProjectFile>("POST", `/api/projects/${projectId}/files`, {
    data,
    file_name: fileName,
    members_only: membersOnly,
  });
}

/**
 * Streamed (multipart) upload — the path the UI uses. The bytes go straight to
 * disk on the server (no base64 in a JSON body), so large files work. Sends a
 * FormData body with a `file` part and a `members_only` text field.
 */
export async function uploadProjectFileMultipart(
  projectId: string,
  file: File,
  membersOnly: boolean,
): Promise<ProjectFile> {
  const fd = new FormData();
  fd.append("members_only", String(membersOnly));
  fd.append("file", file);
  const res = await fetch(
    `${BROWSER_API_BASE_URL}/api/projects/${projectId}/files`,
    { method: "POST", credentials: "include", body: fd },
  );
  let parsed: ApiResponse<ProjectFile>;
  try {
    parsed = (await res.json()) as ApiResponse<ProjectFile>;
  } catch {
    throw new ApiClientError(`Non-JSON response (HTTP ${res.status})`, res.status);
  }
  if (!res.ok || !parsed.success) {
    throw new ApiClientError(
      parsed.success ? `HTTP ${res.status}` : parsed.error,
      res.status,
    );
  }
  return parsed.data;
}
export function listProjectFiles(
  projectId: string,
): Promise<{ files: ProjectFile[] }> {
  return browserRequest<{ files: ProjectFile[] }>(
    "GET",
    `/api/projects/${projectId}/files`,
  );
}
export function deleteProjectFile(
  projectId: string,
  fileId: string,
): Promise<unknown> {
  return browserRequest("DELETE", `/api/projects/${projectId}/files/${fileId}`);
}
